/* eslint-disable warp-drive/no-legacy-request-patterns */

// These are adapter methods (`deleteRecord = deleteRecordWithAuditReason`), so
// the types below describe just the adapter, serializer and snapshot parts
// they use.
// TS-STANDIN(Snapshot): the snapshot that ember-data gives to an adapter.
// The real types need casts for the adapter mixin `this` and for buildURL()
// ids.
interface AuditSnapshot {
  id: string | null;
}

// TS-STANDIN(ModelSchema): the model class that ember-data gives to an adapter.
interface AuditModelClass {
  modelName: string;
}

// The record's serialized payload, which carries the audit reason.
interface AuditPayload {
  auditReason?: string;
}

// TS-STANDIN(service:store): the store and its serializer.
interface AuditStore {
  serializerFor(modelName: string): {
    payloadKeyFromModelName(modelName: string): string;
    serializeIntoHash(
      hash: object,
      type: AuditModelClass,
      snapshot: AuditSnapshot,
    ): void;
  };
}

// TS-STANDIN(RESTAdapter): the adapter that these methods are installed on.
interface AuditAdapter {
  buildURL(
    modelName: string,
    id: string | null,
    snapshot: AuditSnapshot,
    requestType: string,
  ): string;
  ajax(
    url: string,
    method: string,
    options: { data: object },
  ): Promise<unknown>;
}

export function deleteRecordWithAuditReason(
  this: AuditAdapter,
  store: AuditStore,
  type: AuditModelClass,
  snapshot: AuditSnapshot,
): Promise<unknown> {
  const id = snapshot.id;
  const data: Record<string, unknown> = {};
  const serializer = store.serializerFor(type.modelName);
  const key = serializer.payloadKeyFromModelName(type.modelName);

  serializer.serializeIntoHash(data, type, snapshot);
  // Push audit reason from serialized hash into the request's top level.
  // serializeIntoHash() has just written the record's payload under `key`.
  data.reason = (data[key] as AuditPayload).auditReason;
  // Remove the entity's object from request body, as we only need the audit reason.
  delete data[key];

  return this.ajax(
    this.buildURL(type.modelName, id, snapshot, 'deleteRecord'),
    'DELETE',
    { data },
  );
}

export function updateRecordWithAuditReason(
  this: AuditAdapter,
  store: AuditStore,
  type: AuditModelClass,
  snapshot: AuditSnapshot,
): Promise<unknown> {
  const data: Record<string, unknown> = {};
  const serializer = store.serializerFor(type.modelName);
  const key = serializer.payloadKeyFromModelName(type.modelName);

  serializer.serializeIntoHash(data, type, snapshot);

  const id = snapshot.id;
  const url = this.buildURL(type.modelName, id, snapshot, 'updateRecord');

  // Push audit reason from serialized hash into the request's top level.
  // serializeIntoHash() has just written the record's payload under `key`.
  const payload = data[key] as AuditPayload;
  data.reason = payload.auditReason;
  delete payload.auditReason;

  return this.ajax(url, 'PUT', { data });
}
